// stores/offline/ConflictResolver.ts
import localforage from 'localforage';

export class ConflictResolver<T extends { id: string; updatedAt?: string }> {
    constructor(private storeName: string) { }

    async resolve(
        local: T,
        server: T,
        strategy: 'client' | 'server' | 'merge' | 'manual' = 'server'
    ): Promise<T> {
        switch (strategy) {
            case 'client':
                return this.clientWins(local, server);

            case 'server':
                return this.serverWins(local, server);

            case 'merge':
                return this.mergeChanges(local, server);

            case 'manual':
                return this.manualResolve(local, server);

            default:
                return server;
        }
    }

    private clientWins(local: T, server: T): T {
        return {
            ...server,
            ...local,
            id: server.id,
            updatedAt: new Date().toISOString()
        };
    }

    private serverWins(local: T, server: T): T {
        return server;
    }

    private mergeChanges(local: T, server: T): T {
        const merged: any = { ...server };

        // Smart merge based on timestamps
        for (const key in local) {
            if (key.startsWith('_')) continue; // Skip local metadata

            const localValue = (local as any)[key];
            const serverValue = (server as any)[key];

            // If values are different
            if (localValue !== serverValue) {
                // For arrays, merge unique values
                if (Array.isArray(localValue) && Array.isArray(serverValue)) {
                    merged[key] = [...new Set([...serverValue, ...localValue])];
                }
                // For objects, deep merge
                else if (
                    typeof localValue === 'object' &&
                    typeof serverValue === 'object' &&
                    localValue !== null &&
                    serverValue !== null
                ) {
                    merged[key] = this.deepMerge(serverValue, localValue);
                }
                // For primitives, use the most recent
                else {
                    const localTime = (local as any)._localUpdatedAt || 0;
                    const serverTime = server.updatedAt
                        ? new Date(server.updatedAt).getTime()
                        : 0;

                    merged[key] = localTime > serverTime ? localValue : serverValue;
                }
            }
        }

        return merged;
    }

    private deepMerge(target: any, source: any): any {
        const output = { ...target };

        for (const key in source) {
            if (source.hasOwnProperty(key)) {
                if (
                    source[key] &&
                    typeof source[key] === 'object' &&
                    !Array.isArray(source[key])
                ) {
                    output[key] = this.deepMerge(target[key] || {}, source[key]);
                } else {
                    output[key] = source[key];
                }
            }
        }

        return output;
    }

    private async manualResolve(local: T, server: T): Promise<T> {
        // This would trigger a UI for manual conflict resolution
        // For now, default to server wins
        console.warn(`Manual conflict resolution required for ${this.storeName}`, {
            local,
            server
        });

        // Store conflict for later resolution
        await localforage.setItem(
            `conflict_${this.storeName}_${local.id}`,
            { local, server, timestamp: Date.now() }
        );

        return server;
    }
}